import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Menu, User, Calendar, MapPin, Users, Phone, Briefcase, CreditCard,
  Wallet, Building2, DollarSign, Check, FileText, Send, Receipt,
  ChevronRight, Sun, Moon, Loader2, AlertCircle
} from 'lucide-react';
import  API_BASE_URL  from './../../../config/baseApi';

export default function Checkout() {
  const { hash } = useParams();
  const navigate = useNavigate();
  
  const [theme, setTheme] = useState('dark');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  
  const [formData, setFormData] = useState({
    cashPay: '',
    tourOperatorPay: '',
    cardPay: '',
    cardNo: '',
    bank: '',
    cardType: '',
  });

  useEffect(() => {
    if (hash) {
      fetchCheckoutData();
    } else {
      setError('Invalid checkout link - no hash provided');
      setLoading(false);
    }
  }, [hash]);

  const fetchCheckoutData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/checkout/${hash}`, {
        headers: { 'Accept': 'application/json' },
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to load checkout data');
      }

      setCheckoutData(result.data);
      setFormData(prev => ({
        ...prev,
        cashPay: result.data.charges.final_bill.toString(),
        tourOperatorPay: '0',
        cardPay: '0',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddAdvance = async () => {
    const advanceDate = prompt('Enter advance payment date (YYYY-MM-DD):');
    const paymentMode = prompt('Enter payment mode:');
    const paidDetails = prompt('Enter payment details:');
    const paidAmount = prompt('Enter amount:');

    if (!advanceDate || !paymentMode || !paidDetails || !paidAmount) {
      alert('All fields are required');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout/${hash}/advance-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          advance_date: advanceDate,
          payment_mode: paymentMode,
          paid_details: paidDetails,
          paid_amount: parseFloat(paidAmount),
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to add advance payment');
      }

      alert(result.message);
      fetchCheckoutData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleSendEmail = async () => {
    if (!window.confirm('Send bill details to guest email?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout/${hash}/send-email`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to send email');
      }

      alert(result.message);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // ─── PRINT HELPERS ────────────────────────────────────────────────────────────

  const openPrintWindow = (htmlContent) => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const sharedPrintStyles = `
    <style>
      @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body {
        font-family: 'IBM Plex Sans', sans-serif;
        font-size: 13px;
        color: #1a1a2e;
        background: #fff;
        padding: 32px 40px;
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        border-bottom: 3px solid #1a1a2e;
        padding-bottom: 16px;
        margin-bottom: 24px;
      }
      .hotel-name {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 22px;
        font-weight: 600;
        letter-spacing: -0.5px;
        color: #1a1a2e;
      }
      .doc-label {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 11px;
        letter-spacing: 3px;
        text-transform: uppercase;
        color: #666;
        margin-top: 4px;
      }
      .print-date {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 11px;
        color: #888;
        text-align: right;
      }
      .guest-card {
        background: #f8f8fc;
        border: 1px solid #e0e0ef;
        border-radius: 8px;
        padding: 16px 20px;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 16px;
        margin-bottom: 24px;
      }
      .info-block label {
        display: block;
        font-size: 9px;
        letter-spacing: 2px;
        text-transform: uppercase;
        color: #888;
        margin-bottom: 4px;
      }
      .info-block span {
        font-weight: 600;
        font-size: 13px;
        color: #1a1a2e;
      }
      .section-title {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 10px;
        letter-spacing: 2.5px;
        text-transform: uppercase;
        color: #888;
        margin-bottom: 10px;
        padding-bottom: 6px;
        border-bottom: 1px solid #e8e8f0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
      }
      th {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 9px;
        letter-spacing: 1.5px;
        text-transform: uppercase;
        color: #888;
        text-align: left;
        padding: 8px 10px;
        border-bottom: 2px solid #1a1a2e;
      }
      th.right { text-align: right; }
      td {
        padding: 9px 10px;
        border-bottom: 1px solid #f0f0f8;
        color: #1a1a2e;
        font-size: 12.5px;
      }
      td.right { text-align: right; font-family: 'IBM Plex Mono', monospace; }
      tr:last-child td { border-bottom: none; }
      tr:hover td { background: #f8f8fc; }
      .totals-box {
        margin-left: auto;
        width: 320px;
        border: 1px solid #e0e0ef;
        border-radius: 8px;
        overflow: hidden;
        margin-top: 8px;
      }
      .totals-row {
        display: flex;
        justify-content: space-between;
        padding: 10px 16px;
        border-bottom: 1px solid #f0f0f8;
        font-size: 12.5px;
      }
      .totals-row:last-child { border-bottom: none; }
      .totals-row.subtotal {
        background: #f4f4fa;
        font-weight: 600;
      }
      .totals-row.advance {
        color: #1a7a4a;
      }
      .totals-row.final {
        background: #1a1a2e;
        color: #fff;
        font-weight: 600;
        font-size: 14px;
      }
      .totals-val {
        font-family: 'IBM Plex Mono', monospace;
        font-weight: 600;
      }
      .badge {
        display: inline-block;
        background: #e8e8f8;
        color: #3a3a6e;
        font-size: 10px;
        padding: 2px 8px;
        border-radius: 4px;
        font-family: 'IBM Plex Mono', monospace;
        font-weight: 600;
        letter-spacing: 0.5px;
      }
      .footer {
        margin-top: 32px;
        padding-top: 16px;
        border-top: 1px solid #e0e0ef;
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: #aaa;
        font-size: 10px;
        font-family: 'IBM Plex Mono', monospace;
      }
      .empty-row td {
        color: #bbb;
        font-style: italic;
        text-align: center;
        padding: 16px;
      }
      @media print {
        body { padding: 16px 20px; }
        @page { margin: 1cm; }
      }
    </style>
  `;

  const handlePrintSummary = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout/${hash}/print-summary`, {
        headers: { 'Accept': 'application/json' },
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to load summary');
      }

      const { registration, other_charges, advance_payments, total, total_advance, final_bill } = result.data;
      const printDate = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

      const html = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><title>Bill Summary – ${registration.guest_name}</title>${sharedPrintStyles}</head>
<body>
  <div class="header">
    <div>
      <div class="hotel-name">Hotel Management</div>
      <div class="doc-label">Bill Summary</div>
    </div>
    <div class="print-date">
      <div>Printed: ${printDate}</div>
      <div style="margin-top:4px;">Reservation: <strong>${registration.reservation_code}</strong></div>
    </div>
  </div>

  <div class="guest-card">
    <div class="info-block"><label>Guest Name</label><span>${registration.guest_name}</span></div>
    <div class="info-block"><label>Room No.</label><span>${registration.allocated_room_no} <span class="badge">${registration.room_type}</span></span></div>
    <div class="info-block"><label>Nationality</label><span>${registration.guest_country || '—'}</span></div>
    <div class="info-block"><label>Check-in</label><span>${registration.reservation_date}</span></div>
    <div class="info-block"><label>Check-out</label><span>${registration.departure_date}</span></div>
    <div class="info-block"><label>Meal Plan</label><span><span class="badge">${registration.meal_plan}</span></span></div>
    <div class="info-block"><label>Guests (Pax)</label><span>${registration.no_of_pax}</span></div>
    <div class="info-block"><label>Booked Via</label><span>${registration.guest_from_cat || 'Direct'}</span></div>
    <div class="info-block"><label>Contact</label><span>${registration.contact_no || '—'}</span></div>
  </div>

  <div class="section-title">Charge Breakdown</div>
  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th class="right">Amount (LKR)</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Room Charges (${registration.no_of_day} night${registration.no_of_day > 1 ? 's' : ''})</td>
        <td class="right">Rs ${parseFloat(registration.rs).toLocaleString()}</td>
      </tr>
      ${other_charges && other_charges.length > 0
        ? other_charges.map(c => `
          <tr>
            <td>${c.item_name}${c.description ? ' <span style="color:#888;font-size:11px;">– ' + c.description + '</span>' : ''}</td>
            <td class="right">Rs ${parseFloat(c.bill_total).toLocaleString()}</td>
          </tr>`).join('')
        : '<tr class="empty-row"><td colspan="2">No additional charges</td></tr>'
      }
    </tbody>
  </table>

  ${advance_payments && advance_payments.length > 0 ? `
  <div class="section-title">Advance Payments</div>
  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th>Date</th>
        <th class="right">Amount (LKR)</th>
      </tr>
    </thead>
    <tbody>
      ${advance_payments.map(a => `
        <tr>
          <td>${a.item_name}</td>
          <td style="color:#888;">${a.bill_date}</td>
          <td class="right" style="color:#1a7a4a;">– Rs ${Math.abs(parseFloat(a.bill_total)).toLocaleString()}</td>
        </tr>`).join('')}
    </tbody>
  </table>` : ''}

  <div class="totals-box">
    <div class="totals-row subtotal">
      <span>Subtotal</span>
      <span class="totals-val">Rs ${parseFloat(total).toLocaleString()}</span>
    </div>
    ${advance_payments && advance_payments.length > 0 ? `
    <div class="totals-row advance">
      <span>Advance Paid</span>
      <span class="totals-val">– Rs ${Math.abs(parseFloat(total_advance)).toLocaleString()}</span>
    </div>` : ''}
    <div class="totals-row final">
      <span>Amount Due</span>
      <span class="totals-val">Rs ${parseFloat(final_bill).toLocaleString()}</span>
    </div>
  </div>

  <div class="footer">
    <span>Thank you for staying with us.</span>
    <span>${registration.reservation_code} · ${registration.allocated_room_no}</span>
  </div>
</body>
</html>`;

      openPrintWindow(html);
    } catch (err) {
      alert('Error loading summary: ' + err.message);
    }
  };

  const handlePrintDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout/${hash}/print-details`, {
        headers: { 'Accept': 'application/json' },
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to load details');
      }

      const { registration, billing_items, advance_payments, total, total_advance, final_bill } = result.data;
      const printDate = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

      const html = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><title>Itemised Bill – ${registration.guest_name}</title>${sharedPrintStyles}</head>
<body>
  <div class="header">
    <div>
      <div class="hotel-name">Hotel Management</div>
      <div class="doc-label">Itemised Bill — Detailed</div>
    </div>
    <div class="print-date">
      <div>Printed: ${printDate}</div>
      <div style="margin-top:4px;">Reservation: <strong>${registration.reservation_code}</strong></div>
    </div>
  </div>

  <div class="guest-card">
    <div class="info-block"><label>Guest Name</label><span>${registration.guest_name}</span></div>
    <div class="info-block"><label>Room No.</label><span>${registration.allocated_room_no} <span class="badge">${registration.room_type}</span></span></div>
    <div class="info-block"><label>Nationality</label><span>${registration.guest_country || '—'}</span></div>
    <div class="info-block"><label>Check-in</label><span>${registration.reservation_date}</span></div>
    <div class="info-block"><label>Check-out</label><span>${registration.departure_date}</span></div>
    <div class="info-block"><label>Nights</label><span>${registration.no_of_day}</span></div>
    <div class="info-block"><label>Meal Plan</label><span><span class="badge">${registration.meal_plan}</span></span></div>
    <div class="info-block"><label>Adults / Children</label><span>${registration.adults} / ${registration.children}</span></div>
    <div class="info-block"><label>Contact</label><span>${registration.contact_no || '—'}</span></div>
  </div>

  <div class="section-title">All Billing Items</div>
  <table>
    <thead>
      <tr>
        <th>Date</th>
        <th>Item Code</th>
        <th>Description</th>
        <th class="right">Qty</th>
        <th class="right">Unit Price</th>
        <th class="right">Total (LKR)</th>
      </tr>
    </thead>
    <tbody>
      <!-- Room charges row -->
      <tr>
        <td style="color:#888;">${registration.reservation_date}</td>
        <td><span class="badge">ROOM</span></td>
        <td>${registration.room_type} – ${registration.meal_plan} (${registration.no_of_day} night${registration.no_of_day > 1 ? 's' : ''})</td>
        <td class="right">${registration.no_of_day}</td>
        <td class="right">Rs ${(parseFloat(registration.rs) / registration.no_of_day).toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td class="right">Rs ${parseFloat(registration.rs).toLocaleString()}</td>
      </tr>
      ${billing_items && billing_items.length > 0
        ? billing_items.map(item => `
          <tr>
            <td style="color:#888;">${item.bill_date}</td>
            <td><span class="badge">${item.item_code || '—'}</span></td>
            <td>${item.item_name}${item.description ? '<br><span style="color:#aaa;font-size:11px;">' + item.description + '</span>' : ''}</td>
            <td class="right">${item.qty}</td>
            <td class="right">Rs ${parseFloat(item.price).toLocaleString()}</td>
            <td class="right">Rs ${parseFloat(item.bill_total).toLocaleString()}</td>
          </tr>`).join('')
        : '<tr class="empty-row"><td colspan="6">No additional items</td></tr>'
      }
    </tbody>
  </table>

  ${advance_payments && advance_payments.length > 0 ? `
  <div class="section-title">Advance Payments Received</div>
  <table>
    <thead>
      <tr>
        <th>Date</th>
        <th colspan="4">Payment Description</th>
        <th class="right">Amount (LKR)</th>
      </tr>
    </thead>
    <tbody>
      ${advance_payments.map(a => `
        <tr>
          <td style="color:#888;">${a.bill_date}</td>
          <td colspan="4">${a.item_name}</td>
          <td class="right" style="color:#1a7a4a;">– Rs ${Math.abs(parseFloat(a.bill_total)).toLocaleString()}</td>
        </tr>`).join('')}
    </tbody>
  </table>` : ''}

  <div class="totals-box">
    <div class="totals-row">
      <span>Room Charges</span>
      <span class="totals-val">Rs ${parseFloat(registration.rs).toLocaleString()}</span>
    </div>
    <div class="totals-row">
      <span>Other Charges</span>
      <span class="totals-val">Rs ${billing_items ? parseFloat(billing_items.reduce((s,i)=>s+parseFloat(i.bill_total),0)).toLocaleString() : '0'}</span>
    </div>
    <div class="totals-row subtotal">
      <span>Subtotal</span>
      <span class="totals-val">Rs ${parseFloat(total).toLocaleString()}</span>
    </div>
    ${advance_payments && advance_payments.length > 0 ? `
    <div class="totals-row advance">
      <span>Advance Paid</span>
      <span class="totals-val">– Rs ${Math.abs(parseFloat(total_advance)).toLocaleString()}</span>
    </div>` : ''}
    <div class="totals-row final">
      <span>Amount Due</span>
      <span class="totals-val">Rs ${parseFloat(final_bill).toLocaleString()}</span>
    </div>
  </div>

  <div class="footer">
    <span>Thank you for staying with us. We hope to see you again.</span>
    <span>${registration.reservation_code} · ${registration.allocated_room_no}</span>
  </div>
</body>
</html>`;

      openPrintWindow(html);
    } catch (err) {
      alert('Error loading details: ' + err.message);
    }
  };

  // ─── CHECKOUT RECEIPT ─────────────────────────────────────────────────────────

  const printCheckoutReceipt = () => {
    const { registration, charges, currency } = checkoutData;
    const cashPay  = parseFloat(formData.cashPay || 0);
    const tourPay  = parseFloat(formData.tourOperatorPay || 0);
    const cardPay  = parseFloat(formData.cardPay || 0);
    const printDate = new Date().toLocaleString('en-GB', { dateStyle: 'long', timeStyle: 'short' });

    const paymentRows = [
      cashPay > 0 ? `<tr><td>Cash</td><td class="right">Rs ${cashPay.toLocaleString()}</td></tr>` : '',
      tourPay > 0 ? `<tr><td>Tour Operator</td><td class="right">Rs ${tourPay.toLocaleString()}</td></tr>` : '',
      cardPay > 0 ? `<tr>
        <td>Card${formData.cardType ? ' (' + formData.cardType + ')' : ''}
          ${formData.bank ? '<br><span class="muted">' + formData.bank + (formData.cardNo ? ' ···' + formData.cardNo.slice(-4) : '') + '</span>' : ''}
        </td>
        <td class="right">Rs ${cardPay.toLocaleString()}</td>
      </tr>` : '',
    ].filter(Boolean).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Receipt – ${registration.guest_name}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'IBM Plex Sans', sans-serif;
      font-size: 13px;
      color: #1a1a2e;
      background: #fff;
      /* receipt-style narrow width */
      max-width: 380px;
      margin: 0 auto;
      padding: 28px 24px;
    }

    /* ── Header ── */
    .logo {
      font-family: 'IBM Plex Mono', monospace;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: -0.5px;
      text-align: center;
    }
    .doc-label {
      text-align: center;
      font-size: 9px;
      letter-spacing: 3px;
      text-transform: uppercase;
      color: #888;
      margin-top: 4px;
      margin-bottom: 20px;
    }

    /* ── Divider ── */
    .divider {
      border: none;
      border-top: 1px dashed #ccc;
      margin: 14px 0;
    }
    .divider.solid {
      border-top: 2px solid #1a1a2e;
    }

    /* ── Guest block ── */
    .guest-name {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 2px;
    }
    .meta {
      font-size: 11px;
      color: #666;
      line-height: 1.7;
    }
    .badge {
      display: inline-block;
      background: #ebebf8;
      color: #3a3a6e;
      font-size: 9px;
      padding: 1px 6px;
      border-radius: 3px;
      font-family: 'IBM Plex Mono', monospace;
      font-weight: 600;
      letter-spacing: 0.5px;
      vertical-align: middle;
      margin-left: 4px;
    }

    /* ── Tables ── */
    table { width: 100%; border-collapse: collapse; }
    td { padding: 5px 0; vertical-align: top; font-size: 12.5px; }
    td.right { text-align: right; font-family: 'IBM Plex Mono', monospace; white-space: nowrap; }
    .section-label {
      font-size: 9px;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: #aaa;
      padding-bottom: 6px;
      display: block;
    }
    .muted { font-size: 10.5px; color: #999; }

    /* ── Totals ── */
    .total-row td { padding: 4px 0; }
    .total-row.grand td {
      font-family: 'IBM Plex Mono', monospace;
      font-size: 16px;
      font-weight: 600;
      padding-top: 10px;
    }
    .total-row.green td { color: #1a7a4a; }

    /* ── Footer ── */
    .footer {
      text-align: center;
      margin-top: 20px;
      font-size: 10px;
      color: #aaa;
      line-height: 1.8;
      font-family: 'IBM Plex Mono', monospace;
    }
    .thank-you {
      font-size: 13px;
      font-weight: 600;
      color: #1a1a2e;
      text-align: center;
      margin-top: 16px;
      letter-spacing: 0.5px;
    }

    /* ── Stamp ── */
    .stamp {
      border: 2px solid #1a7a4a;
      color: #1a7a4a;
      border-radius: 6px;
      text-align: center;
      padding: 6px 12px;
      font-family: 'IBM Plex Mono', monospace;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 2px;
      text-transform: uppercase;
      margin: 16px auto 0;
      width: fit-content;
    }

    @media print {
      body { padding: 0; }
      @page { margin: 0.5cm; size: 80mm auto; }
    }
  </style>
</head>
<body>

  <div class="logo">Hotel Management</div>
  <div class="doc-label">Guest Receipt</div>

  <hr class="divider solid">

  <div class="guest-name">${registration.guest_name}</div>
  <div class="meta">
    ${registration.guest_country || ''}<br>
    Room ${registration.allocated_room_no}<span class="badge">${registration.room_type}</span><br>
    ${registration.reservation_date} → ${registration.departure_date} &nbsp;·&nbsp; ${registration.no_of_day} night${registration.no_of_day > 1 ? 's' : ''}<br>
    ${registration.meal_plan} &nbsp;·&nbsp; ${registration.no_of_pax} pax<br>
    ${registration.reservation_code}
  </div>

  <hr class="divider">

  <span class="section-label">Charges</span>
  <table>
    <tr>
      <td>Room Charges</td>
      <td class="right">Rs ${charges.room_charges.toLocaleString()}</td>
    </tr>
    ${charges.other_charges_total > 0 ? `<tr>
      <td>Other Charges</td>
      <td class="right">Rs ${charges.other_charges_total.toLocaleString()}</td>
    </tr>` : ''}
    ${Math.abs(charges.advance_total) > 0 ? `<tr>
      <td><span class="muted">Advance Paid</span></td>
      <td class="right" style="color:#1a7a4a;">– Rs ${Math.abs(charges.advance_total).toLocaleString()}</td>
    </tr>` : ''}
  </table>

  <hr class="divider">

  <table>
    <tr class="total-row grand">
      <td>TOTAL DUE</td>
      <td class="right">Rs ${charges.final_bill.toLocaleString()}</td>
    </tr>
    <tr>
      <td class="muted">USD Equivalent</td>
      <td class="right muted">$ ${currency.usd_amount.toFixed(2)}</td>
    </tr>
    <tr>
      <td></td>
      <td class="right" style="font-size:9px;color:#bbb;">@ Rs ${currency.us_rate}/USD</td>
    </tr>
  </table>

  <hr class="divider">

  <span class="section-label">Payment Received</span>
  <table>
    ${paymentRows}
  </table>

  <div class="stamp">✓ PAID IN FULL</div>

  <hr class="divider" style="margin-top:20px;">

  <div class="footer">
    Checked out: ${printDate}<br>
    ${registration.contact_no ? 'Contact: ' + registration.contact_no + '<br>' : ''}
    ${registration.email ? registration.email + '<br>' : ''}
  </div>

  <div class="thank-you">Thank you for your stay!</div>

</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=480,height=700');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  // ─── CHECKOUT ─────────────────────────────────────────────────────────────────

  const handleCheckout = async () => {
    const totalPay = parseFloat(formData.cashPay || 0) + 
                     parseFloat(formData.tourOperatorPay || 0) + 
                     parseFloat(formData.cardPay || 0);
    
    if (Math.abs(totalPay - checkoutData.charges.final_bill) > 0.01) {
      alert(`Total payment (${totalPay}) must equal final bill (${checkoutData.charges.final_bill})`);
      return;
    }

    if (!window.confirm('Are you sure you want to complete checkout? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessing(true);
      
      const response = await fetch(`${API_BASE_URL}/api/checkout/${hash}/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          cash_pay: parseFloat(formData.cashPay || 0),
          tour_pay: parseFloat(formData.tourOperatorPay || 0),
          card_pay: parseFloat(formData.cardPay || 0),
          card_no: formData.cardNo,
          bank: formData.bank,
          card_type: formData.cardType,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Checkout failed');
      }

      // Print receipt before navigating away
      printCheckoutReceipt();

      // Small delay to let print window open before navigating
      setTimeout(() => navigate('/admin/default'), 800);

    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-white/60">Loading checkout details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-4" />
          <p className="text-slate-900 dark:text-white text-xl font-semibold mb-2">Error</p>
          <p className="text-slate-600 dark:text-white/60 mb-4">{error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={fetchCheckoutData}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!checkoutData) return null;

  const { registration, charges, currency } = checkoutData;
  const totalPay = parseFloat(formData.cashPay || 0) + 
                   parseFloat(formData.tourOperatorPay || 0) + 
                   parseFloat(formData.cardPay || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-blue-950 dark:to-slate-900 transition-colors duration-300">

      <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="bg-white/80 dark:bg-white/[0.07] backdrop-blur-2xl rounded-3xl border border-slate-200 dark:border-white/10 p-8 shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/10 to-violet-600/10 dark:from-blue-500/20 dark:to-violet-600/20 flex items-center justify-center border border-blue-500/20">
                    <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-slate-500 dark:text-white/50 text-sm mb-1">Room Number</p>
                    <p className="text-slate-900 dark:text-white text-3xl font-bold">{registration.allocated_room_no}</p>
                    <p className="text-slate-600 dark:text-white/40 text-sm mt-1">{registration.room_type} Room</p>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-600/10 dark:from-emerald-500/20 dark:to-teal-600/20 flex items-center justify-center border border-emerald-500/20">
                    <User className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-slate-500 dark:text-white/50 text-sm mb-1">Guest Name</p>
                    <p className="text-slate-900 dark:text-white text-2xl font-semibold">{registration.guest_name}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <MapPin className="w-4 h-4 text-slate-500 dark:text-white/40" />
                      <p className="text-slate-600 dark:text-white/60 text-sm">{registration.guest_country}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/10 to-red-600/10 dark:from-orange-500/20 dark:to-red-600/20 flex items-center justify-center border border-orange-500/20">
                    <Briefcase className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-slate-500 dark:text-white/50 text-sm mb-1">Booked Via</p>
                    <p className="text-slate-900 dark:text-white text-xl font-semibold">{registration.guest_from_cat || 'Direct'}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Phone className="w-4 h-4 text-slate-500 dark:text-white/40" />
                      <p className="text-slate-600 dark:text-white/60 text-sm">{registration.contact_no}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          <div className="xl:col-span-2 space-y-6">
            <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
              <h3 className="text-slate-900 dark:text-white text-lg font-semibold mb-6 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Stay Information
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-slate-500 dark:text-white/40 text-xs uppercase mb-2">Check-in</p>
                  <p className="text-slate-900 dark:text-white font-medium">{registration.reservation_date}</p>
                </div>
                <div>
                  <p className="text-slate-500 dark:text-white/40 text-xs uppercase mb-2">Check-out</p>
                  <p className="text-slate-900 dark:text-white font-medium">{registration.departure_date}</p>
                </div>
                <div>
                  <p className="text-slate-500 dark:text-white/40 text-xs uppercase mb-2">Meal Plan</p>
                  <span className="inline-flex px-3 py-1 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 text-sm font-medium">
                    {registration.meal_plan}
                  </span>
                </div>
                <div>
                  <p className="text-slate-500 dark:text-white/40 text-xs uppercase mb-2">Guests</p>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-600 dark:text-white/60" />
                    <p className="text-slate-900 dark:text-white font-medium">{registration.no_of_pax}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 dark:bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-white/10 p-6 shadow-lg">
              <h3 className="text-slate-900 dark:text-white text-lg font-semibold mb-6 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                Payment Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    Cash Payment
                  </label>
                  <input
                    type="number"
                    value={formData.cashPay}
                    onChange={(e) => handleInputChange('cashPay', e.target.value)}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
                
                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Tour Operator
                  </label>
                  <input
                    type="number"
                    value={formData.tourOperatorPay}
                    onChange={(e) => handleInputChange('tourOperatorPay', e.target.value)}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
                
                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Card Payment
                  </label>
                  <input
                    type="number"
                    value={formData.cardPay}
                    onChange={(e) => handleInputChange('cardPay', e.target.value)}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Card Number</label>
                  <input
                    type="text"
                    value={formData.cardNo}
                    onChange={(e) => handleInputChange('cardNo', e.target.value)}
                    maxLength={16}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Bank</label>
                  <input
                    type="text"
                    value={formData.bank}
                    onChange={(e) => handleInputChange('bank', e.target.value)}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-slate-600 dark:text-white/60 text-sm mb-2 block">Card Type</label>
                  <input
                    type="text"
                    value={formData.cardType}
                    onChange={(e) => handleInputChange('cardType', e.target.value)}
                    className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-500/10 to-violet-600/10 border border-blue-200 dark:border-blue-500/30 rounded-xl px-6 py-4">
                <p className="text-slate-600 dark:text-white/60 text-sm mb-1">Total Payment</p>
                <p className="text-slate-900 dark:text-white text-3xl font-bold">Rs {totalPay.toLocaleString()}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <button onClick={handleAddAdvance} className="group bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-4 text-slate-900 dark:text-white transition-all">
                <div className="flex flex-col items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium">Advance</span>
                </div>
              </button>
              <button onClick={handlePrintSummary} className="group bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-4 text-slate-900 dark:text-white transition-all">
                <div className="flex flex-col items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-sm font-medium">Summary</span>
                </div>
              </button>
              <button onClick={handlePrintDetails} className="group bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-4 text-slate-900 dark:text-white transition-all">
                <div className="flex flex-col items-center gap-2">
                  <Receipt className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  <span className="text-sm font-medium">Details</span>
                </div>
              </button>
              <button onClick={handleSendEmail} className="group bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-4 text-slate-900 dark:text-white transition-all">
                <div className="flex flex-col items-center gap-2">
                  <Send className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  <span className="text-sm font-medium">Send Bill</span>
                </div>
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white/80  dark:bg-white/[0.04] dark:to-white/[0.02] backdrop-blur-2xl rounded-3xl border border-slate-200 dark:border-white/10 p-6 shadow-xl sticky top-24">
              <h3 className="text-slate-900 dark:text-white text-xl font-bold mb-6 flex items-center gap-2">
                <Receipt className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                Final Bill
              </h3>

              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-slate-200 dark:border-white/10">
                  <span className="text-slate-600 dark:text-white/60">Room Charges</span>
                  <span className="text-slate-900 dark:text-white font-semibold text-lg">Rs {charges.room_charges.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between items-center pb-4 border-b border-slate-200 dark:border-white/10">
                  <span className="text-slate-600 dark:text-white/60">Other Charges</span>
                  <span className="text-slate-900 dark:text-white font-semibold text-lg">Rs {charges.other_charges_total.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between items-center pb-4 border-b border-slate-200 dark:border-white/10">
                  <span className="text-slate-900 dark:text-white font-medium">Subtotal</span>
                  <span className="text-slate-900 dark:text-white font-bold text-xl">Rs {charges.subtotal.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between items-center pb-4 border-b border-slate-200 dark:border-white/10">
                  <span className="text-slate-600 dark:text-white/60">Advance Paid</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">- Rs {Math.abs(charges.advance_total).toLocaleString()}</span>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-500/20 dark:to-orange-600/20 border border-red-200 dark:border-red-500/30 rounded-2xl p-5 mt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-slate-600 dark:text-white/80 text-sm mb-1">Amount Due (LKR)</p>
                      <p className="text-slate-900 dark:text-white text-3xl font-bold">Rs {charges.final_bill.toLocaleString()}</p>
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                      <DollarSign className="w-7 h-7 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-500/20 dark:to-violet-600/20 border border-blue-200 dark:border-blue-500/30 rounded-2xl p-5">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-slate-600 dark:text-white/80 text-sm mb-1">USD Equivalent</p>
                      <p className="text-slate-900 dark:text-white text-2xl font-bold">$ {currency.usd_amount.toFixed(2)}</p>
                      <p className="text-slate-500 dark:text-white/40 text-xs mt-1">Rate: 1 USD = Rs {currency.us_rate}</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={processing || Math.abs(totalPay - charges.final_bill) > 0.01}
                className="w-full mt-8 bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-bold py-5 px-6 rounded-2xl shadow-xl transition-all flex items-center justify-center gap-3 group"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-lg">Processing...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-6 h-6" />
                    <span className="text-lg">Complete Checkout</span>
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}